"use client";
import React, { useState } from "react";
import { RadioGroup, Radio } from "@headlessui/react";
import useTheme from "../../hooks/useTheme";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { useThemeContext } from "../ThemeContext";
import { SimpleProduct } from "../../shared/types";
import { PaymentMethodMessagingElement } from "@stripe/react-stripe-js";

interface ProductSelectProps {
  simpleProducts: SimpleProduct[];
  selectedProductIndex: number | null;
  onProductChange: (index: number) => void;
  currency?: string;
}

const ProductSelectCards: React.FC<ProductSelectProps> = ({
  simpleProducts,
  selectedProductIndex,
  onProductChange,
  currency,
}) => {
  const [selected, setSelected] = useState(
    selectedProductIndex !== null ? simpleProducts[selectedProductIndex] : null
  );

  const handleProductChange = (product: SimpleProduct) => {
    const index = simpleProducts.indexOf(product);
    setSelected(product);
    onProductChange(index);
  };

  const { theme } = useThemeContext();
  const { accent1, accent2 } = useTheme(theme); // Use theme hook

  const numberFormatter = (price: number) => {
    if (price >= 1000 && Number.isInteger(price)) {
      return new Intl.NumberFormat("en-US", {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price);
    }
    return new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }

  return (
    <fieldset aria-label="Product selection">
      <RadioGroup
        value={selected}
        onChange={handleProductChange}
        className="space-y-4"
      >
        {simpleProducts.map((simpleProduct, index) => (
          <Radio
            key={index}
            value={simpleProduct}
            aria-label={simpleProduct.name}
            className="group relative flex justify-between items-center cursor-pointer rounded-lg bg-white px-4 py-4 shadow-sm focus:outline-none data-[focus]:border-indigo-600 data-[focus]:ring-2 data-[focus]:ring-indigo-600 sm:flex sm:justify-between"
          >
            <span className="flex items-start">
              <span className="flex-shrink-0 w-6">
              <CheckCircleIcon
                aria-hidden="true"
                className={`h-5 w-5 m-1 ${accent2} [.group:not([data-checked])_&]:invisible`}
              />
              </span>
              <span className="flex flex-col text-sm align-start">
                <span className="font-medium text-lg text-gray-900">
                  {simpleProduct.name}
                </span>
                {simpleProduct.headline && (
                  <h4 className="text-lg font-bold">
                    {simpleProduct.headline}
                  </h4>
                )}
                {simpleProduct.description && (
                  <p className="text-gray-500">{simpleProduct.description}</p>
                )}
                {simpleProduct.paymentMethodTypes?.includes("affirm") && (
                  <div className="text-sm text-gray-500">
                    <PaymentMethodMessagingElement
                      options={{
                        amount: simpleProduct.price * 100,
                        currency: "USD",
                        countryCode: "US",
                        paymentMethodTypes: ["affirm"],
                      }}
                    />
                  </div>
                )}
              </span>
            </span>
            <span className="flex items-center md:items-end text-sm sm:ml-4 sm:mt-0 md:flex-col sm:text-right">
              <span className="font-bold text-lg text-gray-900">
                {simpleProduct.price && (
                  <span className="mt-2 flex text-sm sm:ml-4 sm:mt-0 sm:flex-col sm:text-right">
                    {simpleProduct.displayPrice ? (
                      <span className="font-bold text-gray-900">
                        {simpleProduct.displayPrice}
                      </span>
                    ) : (
                      <>
                        <span className="font-bold text-gray-900">
                          {currency || "$"}
                          {numberFormatter(simpleProduct.price)}
                        </span>
                        <span className="ml-1 text-gray-500 sm:ml-0">
                          {(currency || "USD").toUpperCase()}
                        </span>
                      </>
                    )}
                  </span>
                )}
                {/* {currency}
                {simpleProduct.price.toFixed(2)} */}
              </span>
              {/* <span className="ml-1 text-gray-500 sm:ml-0">USD</span> */}
            </span>

            <span
              aria-hidden="true"
              className={`pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-[focus]:border ${accent2} `}
            />
          </Radio>
        ))}
      </RadioGroup>
    </fieldset>
  );
};

export default ProductSelectCards;
